import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { resetPassword } from '../features/auth/authApi';
import { PasswordVisibilityToggle } from '../components/ui/Input';

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [newPassword, setNewPassword] = useState('');
  const [isPasswordRevealed, setIsPasswordRevealed] = useState(false);

  const submit = useMutation({
    mutationFn: () => resetPassword(token!, newPassword),
    onSuccess: () => {
      setTimeout(() => navigate('/login'), 2000);
    },
  });

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    submit.mutate();
  }

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper-100 px-4 font-manuscript">
        <div className="w-full max-w-sm rounded-sm border border-manuscript-line-strong bg-paper-50 p-8 text-center">
          <p className="text-sm text-manuscript-ink-soft">{t('auth.resetPassword.invalidLink')}</p>
          <Link to="/forgot-password" className="mt-4 inline-block text-sm font-medium text-seal-500 hover:text-seal-400">
            {t('auth.forgotPassword.title')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-paper-100 px-4 font-manuscript">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, #8c2d1e 1.5px, transparent 0)',
          backgroundSize: '28px 28px',
        }}
      />

      <div className="relative w-full max-w-sm">
        <Link to="/" className="mb-8 flex items-center justify-center gap-2.5">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full">
            <img src="/logo.png" alt={t('common.appName')} className="h-full w-full object-cover" />
          </span>
          <span className="font-display text-lg font-semibold text-manuscript-ink">{t('common.appName')}</span>
        </Link>

        <div className="rounded-sm border border-manuscript-line-strong bg-paper-50 p-8">
          <h1 className="font-display text-xl font-semibold text-manuscript-ink">
            {t('auth.resetPassword.title')}
          </h1>
          <p className="mt-1 text-sm text-manuscript-ink-soft">{t('auth.resetPassword.subtitle')}</p>

          {submit.isSuccess ? (
            <p className="mt-6 text-sm font-medium text-seal-500">{t('auth.resetPassword.success')}</p>
          ) : (
            <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4">
              <div>
                <label htmlFor="newPassword" className="text-sm text-manuscript-ink-soft">
                  {t('auth.resetPassword.newPassword')}
                </label>
                <div className="relative mt-1.5">
                  <input
                    id="newPassword"
                    type={isPasswordRevealed ? 'text' : 'password'}
                    required
                    minLength={8}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full rounded-sm border border-manuscript-line-strong bg-paper-100 px-4 py-2.5 pr-10 text-sm text-manuscript-ink outline-none focus:border-seal-500"
                  />
                  <PasswordVisibilityToggle
                    isRevealed={isPasswordRevealed}
                    onToggle={() => setIsPasswordRevealed((prev) => !prev)}
                  />
                </div>
              </div>

              {submit.isError && <p className="text-sm text-red-600">{t('auth.resetPassword.error')}</p>}

              <button
                type="submit"
                disabled={submit.isPending}
                className="mt-2 flex w-full items-center justify-center rounded-sm bg-seal-500 px-6 py-3 text-sm font-semibold text-paper-50 transition-colors hover:bg-seal-400 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {submit.isPending ? t('common.loading') : t('auth.resetPassword.submit')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
